import { useCallback, useEffect, useRef, useState } from 'react'

import { Button, Container } from '@ui/index'
import pushNotificationsService, { PushNotificationState } from '@services/pushNotifications.service'
import { reportUnexpectedClientError } from '@services/client-error.service'

import css from './notification-settings.module.scss'

type BrowserPermission = NotificationPermission | 'unsupported'
type SettingsState = PushNotificationState & { isError?: boolean }

const getBrowserPermission = (): BrowserPermission => typeof window !== 'undefined' && 'Notification' in window
    ? window.Notification.permission : 'unsupported'

const permissionLabels: Record<BrowserPermission, string> = {
    granted: 'Разрешены',
    default: 'Не запрошено',
    denied: 'Заблокированы',
    unsupported: 'Не поддерживаются',
}

const getSubscriptionLabel = (state: SettingsState | null, error: string) => {
    if (!state) return error ? 'Не удалось проверить' : 'Проверяем…'
    if (state.isEnabled) return 'Подключены'
    if (state.status === 'denied') return 'Заблокированы браузером'
    if (state.status === 'unsupported') return 'Недоступны'
    if (state.isError) return 'Подключение не подтверждено'
    return state.status === 'disabled' ? 'Выключены в CRM' : 'Не включены'
}

/** Постоянная настройка уведомлений только для CRM в текущем браузере. */
export const NotificationSettings = () => {
    const [permission, setPermission] = useState<BrowserPermission>(getBrowserPermission)
    const [state, setState] = useState<SettingsState | null>(null)
    const [isBusy, setBusy] = useState(true)
    const [error, setError] = useState('')
    const mounted = useRef(false)
    const inFlight = useRef(false)
    const requestVersion = useRef(0)

    const runAction = useCallback((operation: () => Promise<SettingsState>, action: string) => {
        if (!mounted.current || inFlight.current) return
        inFlight.current = true
        const version = ++requestVersion.current
        const isCurrent = () => mounted.current && version === requestVersion.current
        setBusy(true)
        setError('')

        // Call the operation in this event stack. In particular, enable() must
        // reach Notification.requestPermission() before any awaited work.
        let pending: Promise<SettingsState>
        try {
            pending = operation()
        } catch (failure) {
            pending = Promise.reject(failure)
        }
        void pending.then(result => {
            if (!isCurrent()) return
            setState(result)
            setPermission(getBrowserPermission())
            if (result.isError) setError(result.message)
        }).catch(failure => {
            if (!isCurrent()) return
            reportUnexpectedClientError(failure, `Profile.notifications.${action}`)
            setState(null)
            setPermission(getBrowserPermission())
            setError('Не удалось обновить настройку уведомлений. Попробуйте ещё раз.')
        }).finally(() => {
            if (!isCurrent()) return
            inFlight.current = false
            setBusy(false)
        })
    }, [])

    const refreshStatus = useCallback(() => {
        runAction(() => pushNotificationsService.getStatus(), 'status')
    }, [runAction])

    useEffect(() => {
        mounted.current = true
        refreshStatus()
        const handleFocus = () => refreshStatus()
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') refreshStatus()
        }
        window.addEventListener('focus', handleFocus)
        document.addEventListener('visibilitychange', handleVisibility)
        return () => {
            mounted.current = false
            requestVersion.current += 1
            inFlight.current = false
            window.removeEventListener('focus', handleFocus)
            document.removeEventListener('visibilitychange', handleVisibility)
        }
    }, [refreshStatus])

    const canEnable = !isBusy && permission !== 'denied' && permission !== 'unsupported'
        && state?.status !== 'unsupported' && !state?.isEnabled
    const canDisable = !isBusy && state?.status !== 'unsupported'
        && (state?.isEnabled || permission === 'granted' && Boolean(state?.isError || error))

    return <Container className={css.card} padding="12px" overflow="visible">
        <section className={css.panel} aria-labelledby="profile-notifications-title" aria-busy={isBusy}>
            <div className={css.heading}>
                <h4 id="profile-notifications-title">Уведомления о заявках</h4>
                <p>Настройки уведомлений CRM доступны здесь в любое время и действуют в этом браузере.</p>
            </div>

            <dl className={css.statuses} aria-live="polite">
                <div><dt>Разрешение браузера</dt><dd>{permissionLabels[permission]}</dd></div>
                <div><dt>Уведомления CRM</dt><dd>{getSubscriptionLabel(state, error)}</dd></div>
            </dl>

            {state?.message && !error && <p className={css.message}>{state.message}</p>}
            {error && <p className={css.error} role="alert">{error}</p>}

            {permission === 'denied'
                ? <p className={css.help}>Разрешите уведомления в настройках сайта возле адресной строки, затем нажмите «Проверить состояние».</p>
                : permission === 'unsupported' || state?.status === 'unsupported'
                    ? <p className={css.help}>Откройте рабочий HTTPS-адрес CRM в браузере с поддержкой уведомлений.</p>
                    : permission === 'granted'
                        ? <p className={css.help}>Разрешение браузера уже выдано. Пункт «Уведомления» в меню сайта показывает сам браузер; подключением CRM можно управлять кнопками ниже.</p>
                        : <p className={css.help}>Пункт «Уведомления» в меню сайта показывает сам браузер. Если его нет, нажмите «Включить уведомления» здесь, чтобы запросить разрешение в этой вкладке CRM.</p>
            }

            <div className={css.actions}>
                <Button size="sm" padding="0 12px" isDisabled={!canEnable}
                    onClick={() => runAction(() => pushNotificationsService.enable(), 'enable')}>
                    Включить уведомления
                </Button>
                <Button size="sm" variant="primaryDark" padding="0 12px" isDisabled={!canDisable}
                    onClick={() => runAction(() => pushNotificationsService.disable(), 'disable')}>
                    Выключить в CRM
                </Button>
                <Button size="sm" variant="primaryDark" padding="0 12px" isDisabled={isBusy} onClick={refreshStatus}>
                    Проверить состояние
                </Button>
            </div>
        </section>
    </Container>
}
